import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listPipelineConfig, updatePipelineConfig, type AdminPipelinesApiError, type PipelineConfig } from '@/api/adminPipelines';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/TextInput';
import styles from '@/features/admin/PipelineRunsPage.module.css';

/*
 * Per-pipeline identifiers (source DB names / schemas / program ids) stored in
 * the reporting DB instead of the workload env. Pipelines read them on every
 * spawn, so a save applies from the NEXT run — no restarts. Empty input =
 * fall back to the env var (shown as the placeholder).
 */
function PipelineConfigCard({ baseUrl, cfg }: { baseUrl: string; cfg: PipelineConfig }) {
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, string | null> = {};
      for (const [key, value] of Object.entries(edits)) {
        const current = cfg.keys.find((k) => k.key === key)?.db_value ?? '';
        if (value === current) continue; // untouched
        patch[key] = value.trim() === '' ? null : value.trim();
      }
      return updatePipelineConfig(baseUrl, cfg.pipeline_name, patch);
    },
    onSuccess: () => {
      setEdits({});
      queryClient.invalidateQueries({ queryKey: ['admin', 'pipeline-config', baseUrl] });
    },
  });

  const dirty = Object.entries(edits).some(([key, value]) => value !== (cfg.keys.find((k) => k.key === key)?.db_value ?? ''));

  return (
    <div className={styles.configCard}>
      <div className={styles.autoRunName}>{cfg.pipeline_name}</div>
      {save.isError && <Alert variant="error">{(save.error as AdminPipelinesApiError)?.message ?? 'Could not save config'}</Alert>}
      {cfg.keys.length === 0 && <div className={styles.muted}>No configurable identifiers — this pipeline takes everything from the workload env.</div>}
      {cfg.keys.map((k) => (
        <div key={k.key} className={styles.configRow}>
          <TextInput
            label={k.key}
            hint={`env: ${k.env_var}`}
            placeholder={k.env_value ?? '(env not set)'}
            value={edits[k.key] ?? k.db_value ?? ''}
            onChange={(e) => setEdits((prev) => ({ ...prev, [k.key]: e.target.value }))}
          />
        </div>
      ))}
      <div className={styles.configFoot}>
        {cfg.updated_by && <span className={styles.muted}>last change: {cfg.updated_by}</span>}
        <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// Client-side mirror of the backend's PIPELINE_CONFIG_KEYS allowlist, used
// ONLY as a read-only fallback so the settings page always shows WHAT is
// configurable — even against a backend that predates the config endpoints or
// hasn't run the pipeline_config migration yet. Values/saving need the
// backend; keep this list in sync when a pipeline is added.
const KNOWN_PIPELINE_KEYS: ReadonlyArray<{ pipeline: string; keys: string[] }> = [
  { pipeline: 'carrefour-spain-world-cup', keys: ['mngs_db', 'mngs_schema', 'mongo_db', 'analytics_program_id', 'prize_program_id'] },
  { pipeline: 'carrefour-france-world-cup', keys: ['mngs_db', 'mngs_schema', 'mongo_db', 'analytics_program_id'] },
  { pipeline: 'carrefour-france-50-3', keys: ['mngs_db', 'mngs_schema', 'mongo_db', 'analytics_program_id'] },
  // Postgres only — cf10 has no DocDB tenant and no analytics program.
  { pipeline: 'carrefour-coca-cola-quick', keys: ['mngs_db', 'mngs_schema'] },
  { pipeline: 'ah-prijsfavorieten', keys: ['mngs_db', 'mngs_schema', 'analytics_program_id'] },
];

export function PipelineConfigPanel({ baseUrl }: { baseUrl: string }) {
  const configQuery = useQuery({
    queryKey: ['admin', 'pipeline-config', baseUrl],
    queryFn: () => listPipelineConfig(baseUrl),
    enabled: baseUrl.length > 0,
  });

  if (configQuery.isLoading) return <div className={styles.stepsState}>Loading pipeline config…</div>;

  // Never blank the settings out: if the backend can't serve config yet, say
  // exactly why saving is unavailable and still show the known pipelines/keys.
  if (configQuery.isError) {
    const status = (configQuery.error as AdminPipelinesApiError)?.status;
    return (
      <div className={styles.autoRun}>
        <div className={styles.autoRunHead}>Pipeline configuration</div>
        <Alert variant="error">
          {status === 404
            ? 'This backend does not expose pipeline configuration yet — deploy the backend version with the config endpoints and run the pipeline_config migration, then reload.'
            : `Could not load config: ${(configQuery.error as AdminPipelinesApiError)?.message ?? 'unknown error'}`}
        </Alert>
        {KNOWN_PIPELINE_KEYS.map((p) => (
          <div key={p.pipeline} className={styles.configCard}>
            <div className={styles.autoRunName}>{p.pipeline}</div>
            <div className={styles.muted}>configurable once available: {p.keys.join(' · ')}</div>
          </div>
        ))}
      </div>
    );
  }

  const { pipelines, hiddenRetired } = configQuery.data!;

  return (
    <div className={styles.autoRun}>
      <div className={styles.autoRunHead}>Pipeline configuration — identifiers only; empty field = env fallback; applies on the next run</div>
      {pipelines.length === 0 && <div className={styles.muted}>No active pipelines on this backend.</div>}
      {pipelines.map((cfg) => (
        <PipelineConfigCard key={cfg.pipeline_name} baseUrl={baseUrl} cfg={cfg} />
      ))}
      {hiddenRetired.length > 0 && (
        <div className={styles.configFootNote}>
          {hiddenRetired.length} retired pipeline{hiddenRetired.length === 1 ? '' : 's'} hidden ({hiddenRetired.join(', ')}) — restore it under Report runs to
          configure it again.
        </div>
      )}
    </div>
  );
}
